import {
  ClientEvent,
  ServerEvent,
  type MatchFinishedMessage,
  type MatchStateMessage,
  type PublicRoom,
  type RoomMode,
} from '@bioforge/protocol';
import { Container, Text, type Graphics } from 'pixi.js';
import { isTextInput, keyToAbilitySlot, keyToCommand } from './input/keyboard.js';
import { BOSS_NAMES, loadout } from './labels.js';
import type { NetClient } from './net/net-client.js';
import { reducedMotion } from './overlays/html.js';
import { StoryOverlay } from './overlays/story-overlay.js';
import { listen, type AppContext, type Navigator, type OnlineIntent, type Scene } from './scene.js';
import {
  DesignFrame,
  currentFrame,
  isPortrait,
  screenBackground,
  type FrameSize,
} from './ui/frame.js';
import { artSprite } from './ui/textures.js';
import { COLOR, textStyle } from './ui/theme.js';
import { NetChip, UiButton, panel } from './ui/widgets.js';
import { MatchResultDialog } from './view/battle-dialogs.js';
import { caps, keyFooter } from './view/battle-panels.js';
import { CoopView } from './view/coop-view.js';
import { DuelView, isBossId, type NetControls } from './view/duel-view.js';
import { LobbyView } from './view/lobby-view.js';
import {
  finishReason,
  clock,
  lobbyIntroIds,
  unseenCards,
  verdictOf,
  verdictTitle,
} from './view/match.js';
import { skinOf } from './view/skins.js';

const SESSION_KEY = 'bioforge.session';
const OPTION_KEYS: readonly string[] = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6'];

interface StoredSession {
  readonly roomCode: string;
  readonly resumeToken: string;
}

export function loadSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function saveSession(session: StoredSession | null): void {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Хранилище недоступно (приватный режим) — переподключение после перезагрузки не сработает.
  }
}

type Phase = 'connecting' | 'lobby' | 'match';

/**
 * Сетевой экран Versus и Co-op (SPEC.md, разделы 8 и 16): лобби комнаты, матч по макетам
 * «Дуэль» и «Кооператив», результаты и повторный матч. Только отображает снимки сервера
 * и отправляет действия — результаты считает сервер. Соединение живёт, пока открыт экран.
 */
export class NetScene implements Scene {
  readonly view: DesignFrame;
  private playerId: string | null = null;
  private room: PublicRoom | null = null;
  private matchId: string | null = null;
  private state: MatchStateMessage | null = null;
  private finished: MatchFinishedMessage | null = null;
  private rematchBy: readonly string[] = [];
  private notice = 'Подключение к серверу…';
  private online = false;
  private countdownEnd: number | null = null;
  private matchStart = 0;
  /** Когда появились текущие варианты Стратега — от этого считается таймер выбора. */
  private optionsKey = '';
  private optionsSince = 0;
  private introduced = false;
  private story: StoryOverlay | null = null;
  private phase: Phase | null = null;
  /** Телефон в портрете (SPEC.md, 16.3). */
  private portrait = false;
  private readonly header = new Container();
  private readonly body = new Container();
  private readonly footer = new Container();
  private readonly noticeText = new Text({
    text: '',
    style: textStyle({ size: 15, weight: '500', color: COLOR.goldSoft, align: 'center' }),
  });
  private readonly chip = new NetChip();
  private lobby: LobbyView | null = null;
  private duel: DuelView | null = null;
  private coop: CoopView | null = null;
  private dialog: MatchResultDialog | null = null;
  private dialogKey = '';
  private headerKey = '';
  private readonly unsubscribe: (() => void)[] = [];
  private readonly timer: ReturnType<typeof setInterval>;

  constructor(
    private readonly nav: Navigator,
    private readonly net: NetClient,
    private readonly intent: OnlineIntent,
    private readonly ctx: AppContext,
  ) {
    this.portrait = isPortrait();
    const frame = currentFrame();
    this.view = new DesignFrame(this.background(frame), frame);
    this.noticeText.anchor.set(0.5, 0);
    this.noticeText.position.set(frame.width / 2, 66);
    this.view.content.addChild(this.body, this.header, this.footer, this.noticeText);
    this.listen();
    this.unsubscribe.push(
      listen('keydown', (e) => this.onKey(e)),
      // Поворот телефона: экран собирается заново, соединение и матч не прерываются.
      listen('resize', () => {
        if (isPortrait() === this.portrait) return;
        this.portrait = isPortrait();
        const next = currentFrame();
        this.view.resize(next, this.background(next));
        this.noticeText.position.set(next.width / 2, 66);
        this.resetViews();
        this.footer.removeChildren().forEach((c) => c.destroy({ children: true }));
        this.headerKey = '';
        this.dialogKey = '';
        this.lobbyKey = '';
        this.render();
      }),
    );
    // Часы матча, отсчёт и таймер выбора Стратега.
    this.timer = setInterval(() => this.render(), 250);
    this.render();
  }

  private get mode(): RoomMode {
    return this.room?.mode ?? (this.intent.kind === 'create' ? this.intent.mode : 'versus');
  }

  private get roomCode(): string | null {
    return this.room?.code ?? (this.intent.kind === 'create' ? null : this.intent.code);
  }

  private get me() {
    return this.room?.players.find((p) => p.playerId === this.playerId) ?? null;
  }

  private listen(): void {
    const s = this.net.socket;
    s.on('connect', () => {
      this.online = true;
      this.enter();
    });
    s.on('connect_error', () => {
      this.online = false;
      this.say('Сервер недоступен. Пробуем подключиться снова…');
    });
    s.on('disconnect', () => {
      this.online = false;
      this.say('Связь потеряна, переподключаемся…');
    });

    const grant = (message: { room: PublicRoom; playerId: string; resumeToken: string }): void => {
      this.playerId = message.playerId;
      this.room = message.room;
      saveSession({ roomCode: message.room.code, resumeToken: message.resumeToken });
      if (message.room.mode === 'versus') this.net.selectLoadout(loadout.duelC, loadout.duelV);
      this.say('');
      this.introduce();
    };
    s.on(ServerEvent.RoomCreated, grant);
    s.on(ServerEvent.RoomJoined, grant);
    s.on(ServerEvent.SessionResumed, (m) => {
      this.playerId = m.playerId;
      this.room = m.room;
      this.say('Сессия восстановлена.');
    });
    s.on(ServerEvent.RoomUpdated, (m) => {
      if (this.room && m.room.revision <= this.room.revision) return;
      const left = (this.room?.players.length ?? 0) > m.room.players.length;
      this.room = m.room;
      if (left) this.notice = 'Второй игрок вышел из комнаты.';
      this.maybeStart();
      this.render();
    });
    s.on(ServerEvent.RoomError, (m) => {
      if (m.event === ClientEvent.SessionResume) {
        saveSession(null);
        this.enterFresh();
        return;
      }
      if (m.code === 'ROOM_NOT_FOUND') {
        this.say('Комната не найдена: код неверный или сервер перезапускался. Esc — в меню.');
        return;
      }
      if (m.code === 'ROLE_TAKEN') {
        this.say('Эту роль уже выбрал второй игрок.');
        return;
      }
      if (m.code === 'ROOM_FULL') {
        this.say('В комнате уже два игрока. Esc — в меню.');
        return;
      }
      this.say(m.message);
    });
    s.on(ServerEvent.ServerError, (m) => this.say(m.message));
    s.on(ServerEvent.ActionRejected, (m) => {
      if (m.code === 'ROLE_FORBIDDEN') this.say('Это действие доступно другой роли.');
      if (m.code === 'STALE_CHOICE') this.say('Выбор уже сделан или устарел.');
    });
    s.on(ServerEvent.MatchCountdown, (m) => {
      this.countdownEnd = performance.now() + m.durationMs;
      this.finished = null;
      this.rematchBy = [];
      this.render();
    });
    s.on(ServerEvent.MatchStarted, (m) => {
      this.matchId = m.matchId;
      this.net.setMatch(m.matchId);
      this.finished = null;
      this.rematchBy = [];
      this.state = null;
      this.countdownEnd = null;
      this.matchStart = performance.now();
      this.resetViews();
      this.say('');
    });
    s.on(ServerEvent.MatchState, (m) => {
      if (m.matchId !== this.matchId && this.matchId !== null) return;
      if (this.state && this.state.matchId === m.matchId && m.revision <= this.state.revision)
        return;
      if (this.matchId === null) this.matchStart = performance.now();
      this.matchId = m.matchId;
      this.net.setMatch(m.matchId);
      this.state = m;
      this.render();
    });
    s.on(ServerEvent.MatchFinished, (m) => {
      this.finished = m;
      this.render();
    });
    s.on(ServerEvent.MatchRematchStatus, (m) => {
      this.rematchBy = m.acceptedBy;
      this.render();
    });
    s.on(ServerEvent.PlayerDisconnected, (m) =>
      this.say(
        this.room?.status === 'playing'
          ? `Второй игрок отключился, ждём ${Math.round(m.graceMs / 1000)} с. Матч на паузе.`
          : 'Второй игрок отключился.',
      ),
    );
    s.on(ServerEvent.PlayerConnected, () => this.say('Второй игрок вернулся.'));
  }

  /** При подключении: своя комната из сессии — восстановить место; иначе войти или создать. */
  private enter(): void {
    const session = loadSession();
    const code = this.roomCode;
    if (code && session?.roomCode === code) {
      this.net.resume(session.resumeToken);
      return;
    }
    this.enterFresh();
  }

  private enterFresh(): void {
    const name = this.ctx.account.name;
    const code = this.roomCode;
    if (code) this.net.joinRoom(code, name);
    else if (this.intent.kind === 'create')
      this.net.createRoom(this.intent.mode, name, this.intent.bossId);
  }

  /** Когда все готовы, старт запрашивает первый игрок комнаты (сервер не создаст второй матч). */
  private maybeStart(): void {
    const room = this.room;
    if (!room || room.status !== 'waiting' || room.players.length < 2) return;
    if (
      room.players.every((p) => p.ready && p.connected) &&
      room.players[0]?.playerId === this.playerId
    ) {
      this.net.requestStart();
    }
  }

  /** Знакомство с новым в лобби (SPEC.md, 15.7): босс, атаки и способности, роли. */
  private introduce(): void {
    const room = this.room;
    if (this.introduced || !room) return;
    this.introduced = true;
    const cards = unseenCards(
      lobbyIntroIds(room, [loadout.duelC, loadout.duelV]),
      this.ctx.content,
      this.ctx.seen,
    );
    if (cards.length === 0) return;
    void this.ctx.seen.mark(
      'intro',
      cards.map((c) => c.id),
    );
    if (!this.ctx.settings.accessibility.hints) return;
    const story = new StoryOverlay({
      content: this.ctx.content,
      cards,
      reducedMotion: reducedMotion(this.ctx.settings.accessibility.reducedMotion),
    });
    this.story = story;
    void story.done.then(() => {
      if (this.story === story) this.story = null;
    });
  }

  private onKey(e: KeyboardEvent): void {
    if (isTextInput(e.target) || this.story) return;
    if (e.code === 'Escape') {
      this.nav.menu();
      return;
    }
    const room = this.room;
    if (e.code === 'Enter') {
      e.preventDefault();
      if (room?.status === 'waiting' && this.me && !(room.mode === 'coop' && !this.me.role)) {
        this.net.setReady(!this.me.ready);
      } else if (this.finished) {
        this.rematch();
      }
      return;
    }
    if (room?.mode === 'coop' && room.status === 'waiting') {
      if (e.code === 'KeyP') this.net.selectRole('pilot');
      if (e.code === 'KeyS') this.net.selectRole('strategist');
      return;
    }
    const slot = keyToAbilitySlot(e.code);
    if (room?.mode === 'versus' && room.status === 'waiting' && slot !== null) {
      // До матча C и V меняют выбранные способности.
      if (slot === 0)
        this.selectLoadout(loadout.duelC === 'cryo' ? 'antibiotic' : 'cryo', loadout.duelV);
      else this.selectLoadout(loadout.duelC, loadout.duelV === 'fever' ? 'immunity' : 'fever');
      return;
    }
    if (room?.status !== 'playing') return;
    if (slot !== null) {
      this.net.sendAction(slot === 0 ? 'ability-c' : 'ability-v');
      return;
    }
    const optionIndex = OPTION_KEYS.indexOf(e.code);
    if (optionIndex >= 0 && room.mode === 'coop') {
      const option = this.state?.state.coop?.options?.[optionIndex];
      if (option) this.net.selectPiece(option.id);
      return;
    }
    const command = keyToCommand(e.code);
    if (command && command !== 'tick') {
      e.preventDefault();
      this.net.sendAction(command);
    }
  }

  private selectLoadout(c: 'cryo' | 'antibiotic', v: 'fever' | 'immunity'): void {
    loadout.duelC = c;
    loadout.duelV = v;
    this.net.selectLoadout(c, v);
    this.render();
  }

  private rematch(): void {
    const finished = this.finished;
    const state = this.rematchState();
    if (finished && (state === 'offer' || state === 'invited')) {
      this.net.requestRematch(finished.matchId);
    }
  }

  private rematchState(): 'offer' | 'waiting' | 'invited' | 'unavailable' {
    if ((this.room?.players.length ?? 0) < 2) return 'unavailable';
    const mine = this.playerId !== null && this.rematchBy.includes(this.playerId);
    if (mine) return 'waiting';
    return this.rematchBy.length > 0 ? 'invited' : 'offer';
  }

  private copyInvite(): void {
    const url = this.inviteUrl();
    if (!navigator.clipboard) {
      this.say(`Ссылка: ${url}`);
      return;
    }
    navigator.clipboard.writeText(url).then(
      () => this.say('Ссылка скопирована — отправьте её второму игроку.'),
      () => this.say(`Ссылка: ${url}`),
    );
  }

  private inviteUrl(): string {
    return `${window.location.origin}${window.location.pathname}?room=${this.room?.code ?? ''}`;
  }

  private say(text: string): void {
    this.notice = text;
    this.render();
  }

  // ——— Отрисовка ———

  private currentPhase(): Phase {
    const room = this.room;
    if (!room) return 'connecting';
    const playing = room.status === 'playing' || room.status === 'finished';
    return playing && this.state ? 'match' : 'lobby';
  }

  private resetViews(): void {
    for (const view of [this.lobby, this.duel, this.coop]) view?.destroy();
    this.lobby = null;
    this.duel = null;
    this.coop = null;
    this.body.removeChildren();
    this.phase = null;
  }

  private render(): void {
    if (this.view.destroyed) return;
    const phase = this.currentPhase();
    if (phase !== this.phase) {
      this.resetViews();
      this.phase = phase;
      this.footer.removeChildren().forEach((c) => c.destroy({ children: true }));
    }
    this.chip.set(this.online ? 'online' : navigator.onLine === false ? 'offline' : 'server-down');
    this.noticeText.text = this.notice;
    this.renderHeader(phase);
    if (phase === 'connecting') this.renderConnecting();
    else if (phase === 'lobby') this.renderLobby();
    else this.renderMatch();
    this.renderDialog();
  }

  private renderConnecting(): void {
    if (this.body.children.length > 0) return;
    const t = new Text({
      text: this.roomCode ? `Входим в комнату ${this.roomCode}…` : 'Создаём комнату…',
      style: textStyle({ size: 24, display: true, weight: '800' }),
    });
    t.anchor.set(0.5);
    t.position.set(this.view.size.width / 2, this.view.size.height / 2);
    this.body.addChild(t);
  }

  private renderLobby(): void {
    const room = this.room;
    if (!room) return;
    if (!this.lobby) {
      this.lobby = new LobbyView(
        {
          copyInvite: () => this.copyInvite(),
          setReady: (ready) => this.net.setReady(ready),
          selectRole: (role) => this.net.selectRole(role),
          selectLoadout: (c, v) => this.selectLoadout(c, v),
        },
        this.portrait,
      );
      this.body.addChild(this.lobby);
    }
    const left =
      this.countdownEnd === null
        ? null
        : Math.max(0, Math.ceil((this.countdownEnd - performance.now()) / 1000));
    const key = JSON.stringify([room, loadout, left]);
    if (key === this.lobbyKey) return;
    this.lobbyKey = key;
    this.lobby.render({
      room,
      playerId: this.playerId,
      inviteUrl: this.inviteUrl(),
      loadout: [loadout.duelC, loadout.duelV],
      catalog: this.ctx.content.catalog,
      countdown: room.status === 'countdown' ? left : null,
    });
  }

  private lobbyKey = '';

  private renderMatch(): void {
    const match = this.state?.state;
    if (!match) return;
    const skin = skinOf(this.ctx.shop);
    if (match.mode === 'versus') {
      const mine = match.boards.find((b) => b.playerId === this.playerId);
      if (!mine) return;
      if (!this.duel) {
        this.duel = new DuelView(this.controls());
        this.body.addChild(this.duel);
      }
      const opponent = this.room?.players.find((p) => p.playerId !== this.playerId) ?? null;
      this.duel.render({
        mine,
        theirs: match.boards.find((b) => b.playerId !== this.playerId) ?? null,
        me: this.me,
        opponent,
        skin,
      });
      if (!this.portrait && this.footer.children.length === 0) {
        this.addFooter(
          keyFooter([
            [['←', '→'], 'двигать'],
            [['Z', 'X'], 'вращать'],
            [['↓'], 'ускорить'],
            [['Пробел'], 'сбросить'],
            [['C', 'V'], 'способности'],
            [['Esc'], 'выйти'],
          ]),
        );
      }
      return;
    }
    const team = match.boards[0];
    const coop = match.coop;
    if (!team || !coop) return;
    if (!this.coop) {
      this.coop = new CoopView(this.controls());
      this.body.addChild(this.coop);
    }
    const key = coop.options ? coop.options.map((o) => o.id).join() : '';
    if (key !== this.optionsKey) {
      this.optionsKey = key;
      this.optionsSince = performance.now();
    }
    this.coop.render({
      team,
      coop,
      players: this.room?.players ?? [],
      playerId: this.playerId,
      choiceLeftMs: Math.max(0, coop.choiceTimeoutMs - (performance.now() - this.optionsSince)),
      skin,
      onChoose: (id) => this.net.selectPiece(id),
    });
    if (!this.portrait && this.footer.children.length === 0) {
      const strategist = coop.strategistId === this.playerId;
      this.addFooter(
        strategist
          ? keyFooter(
              [
                [['1', '2', '3'], 'выбрать капсулу'],
                [['C'], 'сканер'],
                [['Esc'], 'выйти'],
              ],
              'Вы — Стратег:',
            )
          : keyFooter(
              [
                [['←', '→'], 'двигать'],
                [['Z', 'X'], 'вращать'],
                [['↓'], 'ускорить'],
                [['Пробел'], 'сбросить'],
                [['C'], 'криотерапия'],
              ],
              'Вы — Пилот:',
            ),
      );
    }
  }

  private background(frame: FrameSize): Graphics {
    if (frame.width < frame.height) {
      return screenBackground(
        0.5,
        0.5,
        [
          { x: 200, y: 500, r: 380, color: 0x1d4a66, alpha: 0.5 },
          { x: 600, y: 300, r: 300, color: 0x4a1e46, alpha: 0.4 },
        ],
        frame,
      );
    }
    return screenBackground(0.5, 0.5, [
      { x: 430, y: 450, r: 420, color: 0x1d4a66, alpha: 0.5 },
      { x: 1080, y: 450, r: 400, color: 0x4a1e46, alpha: 0.45 },
    ]);
  }

  /** Управление с касаний: те же действия, что и с клавиатуры. */
  private controls(): NetControls {
    return {
      portrait: this.portrait,
      onCommand: (command) => {
        if (this.room?.status === 'playing') this.net.sendAction(command);
      },
      onAbility: (slot) => {
        if (this.room?.status === 'playing')
          this.net.sendAction(slot === 0 ? 'ability-c' : 'ability-v');
      },
    };
  }

  private addFooter(bar: Container): void {
    // На телефоне вместо подсказок клавиш — экранные кнопки.
    if (this.portrait) return;
    bar.position.set((this.view.size.width - bar.width) / 2, this.view.size.height - 18 - 33);
    this.footer.addChild(bar);
  }

  /** Шапка: в лобби — «Меню» и название; в матче — логотип, режим и время, комната, связь, выход. */
  private renderHeader(phase: Phase): void {
    const room = this.room;
    const match = this.state?.state;
    const bossName = room?.bossId && isBossId(room.bossId) ? BOSS_NAMES[room.bossId] : null;
    const modeName = this.mode === 'coop' ? 'Кооператив' : 'Дуэль';
    const elapsed =
      phase === 'match' && !this.finished && !match?.paused
        ? clock(performance.now() - this.matchStart)
        : '';
    const moves = match?.coop?.movesLeft ?? null;
    const key = JSON.stringify([
      phase,
      room?.code,
      bossName,
      modeName,
      elapsed,
      moves,
      this.finished !== null,
    ]);
    if (key === this.headerKey) return;
    this.headerKey = key;
    this.chip.removeFromParent();
    this.header.removeChildren().forEach((c) => c.destroy({ children: true }));
    const root = this.header;

    const width = this.view.size.width;
    const tall = this.portrait;
    if (phase !== 'match') {
      const back = new UiButton({
        width: 104,
        height: 44,
        radius: 12,
        label: 'Меню',
        fontSize: 15,
        center: true,
        icon: { key: 'icon-back', size: 18 },
        onTap: () => this.nav.menu(),
      });
      const side = tall ? 22 : 60;
      back.position.set(side, 22);
      const title = new Text({
        text: tall ? modeName : `${modeName} · комната`,
        style: textStyle({ size: tall ? 24 : 30, display: true, weight: '900', shadow: 3 }),
      });
      title.anchor.set(0, 0.5);
      title.position.set(side + 104 + 18, 44);
      root.addChild(back, title);
      this.chip.position.set(width - side - this.chip.width, 22);
      root.addChild(this.chip);
      return;
    }

    const logo = artSprite('logo', 40);
    logo.position.set(40 + 20, 38);
    const word = new Text({
      text: 'BioForge',
      style: textStyle({ size: 22, display: true, weight: '900', shadow: 3 }),
    });
    const bio = new Text({
      text: 'Bio',
      style: textStyle({ size: 22, display: true, weight: '900', color: COLOR.mint, shadow: 3 }),
    });
    for (const t of [word, bio]) {
      t.anchor.set(0, 0.5);
      t.position.set(92, 36);
    }
    if (tall) logo.position.set(22 + 20, 40);
    else root.addChild(word, bio);
    root.addChild(logo);

    // Режим, босс и ход матча.
    const coop = this.mode === 'coop';
    const chip = new Container();
    const k = caps(bossName ? `${modeName} · ${bossName}` : modeName, coop ? 0x8fe3c9 : COLOR.pink);
    const value = new Text({
      text: coop ? (moves !== null ? `Ходов: ${moves}` : '') : elapsed,
      style: textStyle({ size: coop ? 18 : 22, display: true, weight: '800' }),
    });
    const cw = 22 + k.width + (value.text ? 14 + value.width : 0) + 22;
    chip.addChild(
      panel({
        width: cw,
        height: 48,
        radius: 14,
        ...(coop
          ? { top: 0x123a34, bottom: 0x0a1f24, border: 0x2f8f7a }
          : { top: 0x3a1530, bottom: 0x1e0c24, border: 0x8a2e5e }),
      }),
    );
    k.anchor.set(0, 0.5);
    k.position.set(22, 24);
    value.anchor.set(0, 0.5);
    value.position.set(22 + k.width + 14, 24);
    chip.addChild(k, value);
    // На телефоне чип не должен наезжать на логотип и кнопку выхода.
    const room_ = width - 2 * 90;
    if (cw > room_) chip.scale.set(room_ / cw);
    chip.position.set((width - cw * chip.scale.x) / 2, 14);
    root.addChild(chip);

    // Справа: комната, связь, выход.
    const size = tall ? 64 : 44;
    const exit = new UiButton({
      width: size,
      height: size,
      label: '',
      radius: 12,
      center: true,
      icon: { key: 'icon-exit', size: tall ? 24 : 18 },
      onTap: () => this.nav.menu(),
    });
    exit.accessibleTitle = 'Выйти из матча';
    exit.position.set(width - (tall ? 22 : 40) - size, tall ? 10 : 16);
    if (tall) {
      root.addChild(exit);
      return;
    }
    this.chip.position.set(exit.x - 12 - this.chip.width, 16);
    const code = new Text({
      text: `Комната ${room?.code ?? ''}`,
      style: textStyle({ size: 14, color: COLOR.textMuted }),
    });
    code.anchor.set(1, 0.5);
    code.position.set(this.chip.x - 14, 38);
    root.addChild(code, this.chip, exit);
  }

  private renderDialog(): void {
    const finished = this.finished;
    const show = finished !== null && this.phase === 'match';
    const rematch = this.rematchState();
    const key = show ? JSON.stringify([finished.matchId, rematch]) : '';
    if (key === this.dialogKey) return;
    this.dialogKey = key;
    this.dialog?.destroy();
    this.dialog = null;
    if (!show) return;
    const coop = this.mode === 'coop';
    const verdict = verdictOf(finished, this.playerId);
    const nameOf = (id: string) =>
      id === this.playerId
        ? 'Вы'
        : (this.room?.players.find((p) => p.playerId === id)?.name ?? 'Соперник');
    const rows = finished.results.map((r) => ({
      name: coop ? 'Команда' : nameOf(r.playerId),
      me: coop || r.playerId === this.playerId,
      score: r.score,
      bestChain: r.bestChain,
      pieces: r.piecesLocked,
    }));
    this.dialog = new MatchResultDialog({
      title: verdictTitle(verdict, coop),
      won: verdict === 'won',
      reason: finishReason(finished.reason, verdict, coop),
      rows,
      rematch,
      onRematch: () => this.rematch(),
      onMenu: () => this.nav.menu(),
      frame: this.view.size,
    });
    this.view.content.addChild(this.dialog);
  }

  /** Выход в меню: покинуть комнату, забыть сессию и закрыть соединение. */
  destroy(): void {
    clearInterval(this.timer);
    this.story?.destroy();
    if (this.room) this.net.leaveRoom();
    saveSession(null);
    this.net.close();
    this.unsubscribe.forEach((off) => off());
    this.resetViews();
    this.dialog?.destroy();
    this.view.destroy();
  }
}
